import React, { useState } from 'react';
import { FileText, Calendar, Clock, Upload, CheckCircle, AlertCircle, Download, Eye, Filter } from 'lucide-react';

const assignmentsData = [
  {
    id: 1,
    title: 'Quadratic Equations Problem Set',
    subject: 'Mathematics',
    teacher: 'Mr. Johnson',
    assignedDate: 'Jan 10, 2026',
    dueDate: 'Jan 20, 2026',
    status: 'pending',
    description: 'Solve problems 1-20 from Chapter 5. Show all working steps.',
    attachments: ['Chapter5_Problems.pdf'],
    maxMarks: 50,
  },
  {
    id: 2,
    title: 'Newton\'s Laws Lab Report',
    subject: 'Physics',
    teacher: 'Mrs. Smith',
    assignedDate: 'Jan 8, 2026',
    dueDate: 'Jan 18, 2026',
    status: 'submitted',
    submittedDate: 'Jan 16, 2026',
    description: 'Write a detailed lab report on the experiment conducted on Newton\'s Laws of Motion.',
    attachments: ['Lab_Report_Template.docx'],
    maxMarks: 30,
    obtainedMarks: null,
  },
  {
    id: 3,
    title: 'Essay: Climate Change Impact',
    subject: 'English',
    teacher: 'Ms. Davis',
    assignedDate: 'Jan 5, 2026',
    dueDate: 'Jan 15, 2026',
    status: 'graded',
    submittedDate: 'Jan 14, 2026',
    description: 'Write a 1000-word essay on the impact of climate change on biodiversity.',
    attachments: ['Essay_Guidelines.pdf'],
    maxMarks: 40,
    obtainedMarks: 36,
    feedback: 'Excellent analysis and well-structured arguments. Good use of references.',
  },
  {
    id: 4,
    title: 'Organic Chemistry Worksheet',
    subject: 'Chemistry',
    teacher: 'Mr. Wilson',
    assignedDate: 'Jan 12, 2026',
    dueDate: 'Jan 22, 2026',
    status: 'pending',
    description: 'Complete the worksheet on organic compounds and their reactions.',
    attachments: ['Organic_Chemistry_Worksheet.pdf'],
    maxMarks: 25,
  },
  {
    id: 5,
    title: 'Cell Biology Diagram',
    subject: 'Biology',
    teacher: 'Mrs. Anderson',
    assignedDate: 'Jan 3, 2026',
    dueDate: 'Jan 13, 2026',
    status: 'graded',
    submittedDate: 'Jan 12, 2026',
    description: 'Draw and label the structure of animal and plant cells.',
    attachments: [],
    maxMarks: 20,
    obtainedMarks: 18,
    feedback: 'Very neat diagrams with accurate labeling.',
  },
  {
    id: 6,
    title: 'World War II Timeline',
    subject: 'History',
    teacher: 'Mr. Brown',
    assignedDate: 'Jan 1, 2026',
    dueDate: 'Jan 11, 2026',
    status: 'overdue',
    description: 'Create a detailed timeline of major events during World War II (1939-1945).',
    attachments: ['Timeline_Template.docx'],
    maxMarks: 30,
  },
];

const filters = ['All', 'Pending', 'Submitted', 'Graded', 'Overdue'];

const Assignments = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const filteredAssignments = assignmentsData.filter((assignment) => {
    if (activeFilter === 'All') return true;
    return assignment.status.toLowerCase() === activeFilter.toLowerCase();
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' };
      case 'submitted':
        return { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Submitted' };
      case 'graded':
        return { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Graded' };
      case 'overdue':
        return { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Overdue' };
      default:
        return { color: 'bg-gray-100 text-gray-700', icon: FileText, label: status };
    }
  };

  const getSubjectColor = (subject) => {
    const colors = {
      'Mathematics': 'bg-blue-500',
      'Physics': 'bg-purple-500',
      'Chemistry': 'bg-orange-500',
      'English': 'bg-green-500',
      'Biology': 'bg-pink-500',
      'History': 'bg-red-500',
    };
    return colors[subject] || 'bg-gray-500';
  };

  const stats = {
    total: assignmentsData.length,
    pending: assignmentsData.filter(a => a.status === 'pending').length,
    submitted: assignmentsData.filter(a => a.status === 'submitted').length,
    graded: assignmentsData.filter(a => a.status === 'graded').length,
    overdue: assignmentsData.filter(a => a.status === 'overdue').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
          <p className="text-gray-600 mt-1">View and submit your assignments</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.submitted}</p>
          <p className="text-sm text-gray-500">Submitted</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.graded}</p>
          <p className="text-sm text-gray-500">Graded</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          <p className="text-sm text-gray-500">Overdue</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter size={18} className="text-gray-400 flex-shrink-0" />
        {filters.map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeFilter === filter
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Assignments List */}
      <div className="space-y-4">
        {filteredAssignments.map((assignment) => {
          const statusConfig = getStatusConfig(assignment.status);
          const StatusIcon = statusConfig.icon;

          return (
            <div
              key={assignment.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-1 h-full min-h-[80px] rounded-full ${getSubjectColor(assignment.subject)} hidden sm:block`} />
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`w-2 h-2 rounded-full ${getSubjectColor(assignment.subject)} sm:hidden`} />
                          <h3 className="font-semibold text-gray-900">{assignment.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{assignment.subject} • {assignment.teacher}</p>
                        <p className="text-sm text-gray-600 mt-2">{assignment.description}</p>
                      </div>
                      <div className="text-sm text-right flex-shrink-0">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Calendar size={14} />
                          <span>Due: {assignment.dueDate}</span>
                        </div>
                        <p className="text-gray-400 mt-1">Max Marks: {assignment.maxMarks}</p>
                        {assignment.status === 'graded' && (
                          <p className="text-green-600 font-semibold mt-1">
                            Score: {assignment.obtainedMarks}/{assignment.maxMarks}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Attachments & Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 flex-wrap">
                        {assignment.attachments?.map((file, index) => (
                          <button
                            key={index}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-600 hover:bg-gray-200 transition-colors"
                          >
                            <Download size={14} />
                            <span>{file}</span>
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        {assignment.status === 'pending' && (
                          <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
                            <Upload size={16} />
                            <span>Submit</span>
                          </button>
                        )}
                        {assignment.status === 'graded' && (
                          <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                            <Eye size={16} />
                            <span>View Feedback</span>
                          </button>
                        )}
                        {assignment.status === 'overdue' && (
                          <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
                            <Upload size={16} />
                            <span>Submit Late</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Feedback for graded */}
                    {assignment.status === 'graded' && assignment.feedback && (
                      <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm font-medium text-green-800">Teacher's Feedback:</p>
                        <p className="text-sm text-green-700 mt-1">{assignment.feedback}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAssignments.length === 0 && (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No assignments found for this filter.</p>
        </div>
      )}
    </div>
  );
};

export default Assignments;
